import { AnimatePresence, Variants, motion } from 'framer-motion';
import { ReactNode } from 'react';

import useInertiaEvent from '../../hooks/inertia/useInertiaEvent';
import useDismissableProp from '../../hooks/react/useDismissableProp';
import useTimeout from '../../hooks/useTimeout';
import CleanButton from '../CleanButton/CleanButton';
import SVG from '../SVG/SVG';
import Stack from '../Stack/Stack';
import Text from '../Text/Text';

import checkmarkIcon from './graphics/checkmark.svg?raw';
import calculateHideTimeout from './helpers/calculateHideTimeout';

import styles from './Toaster.module.css';

const variants: Variants = {
    hidden: { opacity: 0, y: 100 },
    visible: { opacity: 1, y: 0 },
};

type ToasterProps = {
    children: ReactNode;
};

const Toaster = ({ children }: ToasterProps) => {
    const { value, dismiss, reset } = useDismissableProp(
        {
            prop: children,
            onDismissed: () => {
                cancelTimeout();
            },
        },
        [children]
    );

    useInertiaEvent('finish', reset);

    const { reset: resetTimeout, cancel: cancelTimeout } = useTimeout(
        dismiss,
        calculateHideTimeout(children)
    );

    return (
        <AnimatePresence>
            {value && (
                <motion.div
                    className={styles.Root}
                    variants={variants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    transition={{ duration: 0.2 }}
                >
                    <CleanButton
                        className={styles.Button}
                        onClick={dismiss}
                        onMouseEnter={cancelTimeout}
                        onMouseLeave={resetTimeout}
                    >
                        <Stack>
                            <div className={styles.Graphic}>
                                <SVG
                                    src={checkmarkIcon}
                                    className={styles.Icon}
                                />
                            </div>

                            <Text>{value}</Text>
                        </Stack>
                    </CleanButton>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Toaster;
